import { useCallback, useMemo, useState } from "react";
import { Button, Flex } from "@radix-ui/themes";
import { useQuery } from "react-query";

import { useReleaseConversationApi } from "@/v2/entities/release-conversation/ReleaseConversation.api";
import {
  ReleaseConversationFilters,
  ReleaseConversation,
} from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { ExploreFiltersValue } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { InteractionType } from "@/routes/simulator-live-mode/types";

import { useSimulator } from "../../context/SimulatorWrapper";

import { TrainingPanelInteractionCard } from "./TrainingPanelInteractionCard";
import { InteractionListView, InteractionListItems } from "./shared";
import { NavigateToTranscriptCallback } from "./TopicsList.shared.types";

interface SamplesTabProps {
  selectedTopic: Topic | null;
  onNavigateToTranscript: NavigateToTranscriptCallback;
  onResetNavigation: () => void;
  onBack: () => void;
  showBackButton: boolean;
}

export const SamplesTab = ({
  selectedTopic,
  onNavigateToTranscript,
  onResetNavigation,
  onBack,
  showBackButton,
}: SamplesTabProps) => {
  const [page, setPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState<ExploreFiltersValue>({});
  const [search, setSearch] = useState("");
  const limit = 50;
  const releaseConversationApi = useReleaseConversationApi();
  const { releasesWithVersions, topics } = useSimulator();

  const topicIds = useMemo(() => {
    if (selectedTopic) {
      return [selectedTopic.id];
    }
    return topics?.map((topic) => topic.id) || [];
  }, [selectedTopic, topics]);

  const filters: ReleaseConversationFilters = useMemo(() => {
    const baseFilters: ReleaseConversationFilters = {
      page,
      limit,
      releaseIds: releasesWithVersions?.map((r) => r.id) || [],
      topicIds: topicIds.length > 0 ? topicIds : undefined,
    };

    if (search) {
      baseFilters.commentFilter = search;
    }

    return baseFilters;
  }, [page, limit, topicIds, search, releasesWithVersions]);

  const { data: sessionsData, isLoading: isLoadingSessions } = useQuery({
    queryKey: ["release-conversations", filters, selectedTopic?.id],
    queryFn: () => releaseConversationApi.getReleaseConversations(filters),
    enabled: true,
  });

  const getInteractionType = useCallback((session: ReleaseConversation) => {
    if (session.ticketId || session.ticketInTrainingId) {
      return "tickets";
    }
    if (session.conversationId || session.conversationInTrainingId) {
      return "conversations";
    }
    return null;
  }, []);

  const handleSessionClick = useCallback(
    (session: ReleaseConversation) => {
      const type = getInteractionType(session);
      if (!type) {
        return;
      }
      const interactionType =
        type === "tickets" ? InteractionType.TICKET : InteractionType.CONVERSATION;
      onNavigateToTranscript(
        session.trainingSessionId,
        `session-${JSON.stringify(session)}`,
        interactionType
      );
    },
    [onNavigateToTranscript, getInteractionType]
  );

  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const hasMorePages = useMemo(() => {
    return (
      sessionsData?.pagination &&
      sessionsData.pagination.page < sessionsData.pagination.totalPages
    );
  }, [sessionsData?.pagination]);

  const isPendingReview = useCallback((session: ReleaseConversation) => {
    return !!(session.ticketInTrainingId || session.conversationInTrainingId);
  }, []);

  const sharedFilter = useMemo<Filter | undefined>(() => {
    return undefined;
  }, []);

  return (
    <Flex direction="column" gap="2" style={{ height: "100%", flex: 1 }}>
      <InteractionListView
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        filter={sharedFilter}
        isLoading={isLoadingSessions}
        isEmpty={
          !sessionsData?.conversations || sessionsData.conversations.length === 0
        }
        loadingText="Loading sessions..."
        emptyText="No sessions found"
        search={search}
        onSearchChanged={(value) => {
          setSearch(value);
          setPage(1);
        }}
        showSearchAndFilters={false}
        allowedFilterFields={[]}
        customFilterOptions={{}}
      >
        {sessionsData?.conversations && sessionsData.conversations.length > 0 && (
          <>
            <InteractionListItems>
              {sessionsData.conversations.map((session) => (
                <TrainingPanelInteractionCard
                  key={session.id}
                  interaction={{
                    interactionType: "conversations",
                    id: session.trainingSessionId,
                    displayId: session.quackUserId
                      ? `${session.id} By ${session.quackUserId}`
                      : `${session.id}`,
                    subject:
                      session.releaseComments?.[0]?.comment ||
                      session.feedbackComment ||
                      `Session ${session.trainingSessionId}`,
                    description:
                      session.releaseComments?.[0]?.comment ||
                      session.feedbackComment ||
                      `Session ${session.trainingSessionId}`,
                    topic: session?.topicId
                      ? topics?.find((t: Topic) => t.id === session.topicId)
                          ?.topic || ""
                      : "",
                    category: session.feedback !== "NONE" ? session.feedback : "",
                    createdAt: session.createdAt,
                    updatedAt: session.updatedAt,
                    status: "ACTIVE",
                    channel: "simulator",
                    countPublicMessages: session.releaseComments?.length || 0,
                    countInternalMessages: 0,
                  }}
                  submitter={`Version ${
                    releasesWithVersions?.find((r) => r.id === session.releaseId)
                      ?.version || ""
                  }`}
                  interactionType={getInteractionType(session)}
                  onClick={() => handleSessionClick(session)}
                  isPendingReview={isPendingReview(session)}
                />
              ))}
            </InteractionListItems>

            {hasMorePages && (
              <Button
                variant="soft"
                onClick={handleLoadMore}
                disabled={isLoadingSessions}
              >
                {isLoadingSessions ? "Loading..." : "Load More"}
              </Button>
            )}
          </>
        )}
      </InteractionListView>
    </Flex>
  );
};

